import React, { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';

/* ── Company details from env ─────────────────────────────────────────────── */
const CO = {
  name:    process.env.REACT_APP_CO_NAME             || 'Social Partners',
  phone:   process.env.REACT_APP_CO_PHONE            || '+91 83000 47458',
  phone2:  process.env.REACT_APP_CO_PHONE2           || '+91 80124 38632',
  address: process.env.REACT_APP_CO_ADDRESS          || 'Erode, Tamil Nadu, India',
  udyam:   process.env.REACT_APP_CO_UDYAM            || 'UDYAM-TN-07-0115553',
  website: process.env.REACT_APP_CO_WEBSITE          || 'www.socialpartners.in',
  email:   process.env.REACT_APP_CO_EMAIL            || 'contact@socialpartners.in',
  bank: {
    accountName: process.env.REACT_APP_CO_BANK_ACCOUNT_NAME || 'DHINESH RAAJ P',
    accountNo:   process.env.REACT_APP_CO_BANK_ACCOUNT_NO   || '0023053000013477',
    ifsc:        process.env.REACT_APP_CO_BANK_IFSC          || 'SIBL0000023',
    bankName:    process.env.REACT_APP_CO_BANK_NAME          || 'South Indian Bank',
    gpay:        process.env.REACT_APP_CO_BANK_GPAY          || '8300445569',
  },
};

const DOC_CFG = {
  quotation: { label: 'QUOTATION', showTerms: true,  showAdvance: false },
  invoice:   { label: 'INVOICE',   showTerms: false, showAdvance: true  },
};

const BADGE_COLOR = {
  quotation: { bg: '#e0e7ff', color: '#0a1f5c' },
  invoice:   { bg: '#fee2e2', color: '#7c2d12' },
};

const fmt = (n) =>
  '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const fmtDocDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};

const calcTotals = (items, advance) => {
  const subtotal = (items || []).reduce(
    (s, r) => s + (parseFloat(r.qty) || 0) * (parseFloat(r.price) || 0), 0
  );
  const adv = parseFloat(advance) || 0;
  return { subtotal, advance: adv, balance: subtotal - adv };
};

/* ── A4 Printable Document ───────────────────────────────────────────────── */
const PrintDoc = React.forwardRef(function PrintDoc({ doc }, ref) {
  const cfg = DOC_CFG[doc.type] || DOC_CFG.quotation;
  const { subtotal, advance, balance } = calcTotals(doc.items, doc.advance);

  const hdrCell = { padding: '11px 16px', fontSize: 15, fontWeight: 700, color: '#fff', background: '#1a3a8f', textAlign: 'left' };
  const cell    = { padding: '10px 16px', fontSize: 15, borderBottom: '1px solid #e5e7eb', color: '#1f2937' };

  const displayItems = [...(doc.items || [])];
  while (displayItems.length < 4) displayItems.push({ id: 'pad' + displayItems.length, _pad: true });

  return (
    <div ref={ref} style={{
      width: '210mm', height: '297mm', background: '#fff', position: 'relative',
      fontFamily: "'Segoe UI', Arial, sans-serif", boxSizing: 'border-box',
      border: 'none', outline: 'none', overflow: 'hidden',
    }}>

      {/* Blue gradient header */}
      <div style={{
        background: 'linear-gradient(135deg, #0a1f5c 0%, #1a3a9e 55%, #2563eb 100%)',
        padding: '30px 50px 60px', position: 'relative', zIndex: 2,
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,.05)' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
          <div style={{ color: '#fff', fontSize: 56, fontWeight: 900, letterSpacing: -1, lineHeight: 1 }}>
            {cfg.label}
          </div>
          <div style={{ color: '#fff', textAlign: 'right' }}>
            <div style={{ fontSize: 12, opacity: .75, textTransform: 'uppercase', letterSpacing: 1.4 }}>Document No.</div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 3 }}>{doc.number}</div>
          </div>
        </div>
        <svg viewBox="0 0 794 60" style={{ position: 'absolute', bottom: -4, left: 0, width: '100%', display: 'block' }}>
          <path d="M0,60 L0,35 Q200,0 397,32 Q594,60 794,22 L794,60 Z" fill="#fff" />
        </svg>
      </div>

      {/* Body */}
      <div style={{ padding: '0 50px 90px', position: 'relative', zIndex: 1, marginTop: -4, background: '#fff' }}>

        {/* Bill To / From */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18, marginTop: 20 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 19, fontWeight: 800, color: '#0a1f5c', marginBottom: 6 }}>Bill To:</div>
            <div style={{ fontSize: 15, color: '#374151', lineHeight: 1.75 }}>
              <div style={{ fontWeight: 700 }}>{doc.customer.name || '—'}</div>
              {doc.customer.phone   && <div>{doc.customer.phone}</div>}
              {doc.customer.address && <div style={{ whiteSpace: 'pre-line' }}>{doc.customer.address}</div>}
            </div>
            <div style={{ marginTop: 8, fontSize: 15, color: '#374151' }}>
              <b>Date:</b> {fmtDocDate(doc.date)}
            </div>
          </div>
          <div style={{ flex: 1, textAlign: 'right' }}>
            <div style={{ fontSize: 19, fontWeight: 800, color: '#0a1f5c', marginBottom: 6 }}>From:</div>
            <div style={{ fontSize: 15, color: '#374151', lineHeight: 1.75 }}>
              <div style={{ fontWeight: 700 }}>{CO.name}</div>
              <div>{CO.phone}</div>
              <div>{CO.address}</div>
              <div>{CO.udyam}</div>
            </div>
          </div>
        </div>

        {/* Items table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden' }}>
          <thead>
            <tr>
              <th style={{ ...hdrCell, width: '55%', borderRadius: '8px 0 0 0' }}>Description</th>
              <th style={{ ...hdrCell, textAlign: 'center', width: '12%' }}>Qty</th>
              <th style={{ ...hdrCell, textAlign: 'right', width: '16%' }}>Price</th>
              <th style={{ ...hdrCell, textAlign: 'right', width: '17%', borderRadius: '0 8px 0 0' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {displayItems.map((item, i) => {
              const rowTotal = (parseFloat(item.qty) || 0) * (parseFloat(item.price) || 0);
              return (
                <tr key={item.id || i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                  <td style={cell}>{item._pad ? '' : (item.description || '—')}</td>
                  <td style={{ ...cell, textAlign: 'center' }}>{item._pad ? '' : item.qty}</td>
                  <td style={{ ...cell, textAlign: 'right' }}>{item._pad ? '' : (item.price ? fmt(item.price) : '—')}</td>
                  <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>{item._pad ? '' : (rowTotal ? fmt(rowTotal) : '—')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
          <div style={{ width: 290 }}>
            {cfg.showAdvance && advance > 0 ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px', background: '#f3f4f6', fontSize: 14, color: '#374151', borderRadius: '8px 8px 0 0', border: '1px solid #e5e7eb' }}>
                  <span style={{ fontWeight: 600 }}>Total</span>
                  <span style={{ fontWeight: 700 }}>{fmt(subtotal)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px', background: '#fef9c3', fontSize: 14, color: '#374151', border: '1px solid #e5e7eb', borderTop: 'none' }}>
                  <span style={{ fontWeight: 600 }}>Advance Paid (-)</span>
                  <span style={{ fontWeight: 700, color: '#d97706' }}>{fmt(advance)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', background: '#1a3a8f', borderRadius: '0 0 8px 8px', fontSize: 15 }}>
                  <span style={{ fontWeight: 700, color: '#fff' }}>Balance</span>
                  <span style={{ fontWeight: 800, color: '#fff' }}>{fmt(balance)}</span>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', background: '#1a3a8f', borderRadius: 8, fontSize: 15 }}>
                <span style={{ fontWeight: 700, color: '#fff' }}>Sub Total</span>
                <span style={{ fontWeight: 800, color: '#fff' }}>{fmt(subtotal)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Terms + Bank */}
        <div style={{ display: 'flex', gap: 30, marginBottom: 16 }}>
          {cfg.showTerms && (doc.terms || []).length > 0 && (
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#0a1f5c', marginBottom: 8 }}>Terms &amp; Conditions:</div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {doc.terms.filter(Boolean).map((t, i) => (
                  <li key={i} style={{ fontSize: 14, color: '#374151', marginBottom: 5, display: 'flex', gap: 6 }}>
                    <span style={{ color: '#1a3a8f', fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>◆</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div style={{ flex: 1, maxWidth: cfg.showTerms ? undefined : 300 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0a1f5c', marginBottom: 8 }}>Bank Account Details</div>
            <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.85 }}>
              <div><b>Account Name:</b> {CO.bank.accountName}</div>
              <div><b>Account Number:</b> {CO.bank.accountNo}</div>
              <div><b>IFSC:</b> {CO.bank.ifsc}</div>
              <div><b>Bank Name:</b> {CO.bank.bankName}</div>
              <div><b>GPAY:</b> {CO.bank.gpay}</div>
            </div>
          </div>
        </div>

        {/* Thank You */}
        <div style={{ textAlign: 'center', padding: '6px 0 10px' }}>
          <span style={{ fontSize: 30, fontWeight: 700, color: '#1a3a8f', fontStyle: 'italic', letterSpacing: 1 }}>
            Thank You!
          </span>
        </div>
      </div>

      {/* Footer bar */}
      <div style={{
        background: '#0a1f5c', color: '#fff', fontSize: 13,
        display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 32,
        padding: '14px 50px', position: 'absolute', bottom: 0, left: 0, right: 0,
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <i className="fas fa-globe" style={{ fontSize: 13 }}></i>{CO.website}
        </span>
        <span style={{ opacity: .35, fontSize: 18 }}>|</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <i className="fas fa-envelope" style={{ fontSize: 13 }}></i>{CO.email}
        </span>
        <span style={{ opacity: .35, fontSize: 18 }}>|</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <i className="fas fa-phone-alt" style={{ fontSize: 13 }}></i>
          {CO.phone} &nbsp;|&nbsp; {CO.phone2}
        </span>
      </div>
    </div>
  );
});

/* ── Modal wrapper ───────────────────────────────────────────────────────── */
export default function DocViewModal({ doc: rawDoc, onClose }) {
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: rawDoc?.number || 'document',
    pageStyle: `
      @page { size: A4 portrait; margin: 0; }
      html, body { margin: 0 !important; padding: 0 !important; width: 210mm !important; height: 297mm !important; overflow: hidden !important; background: white !important; }
      body > div:first-child { position: fixed !important; top: 0 !important; left: 0 !important; right: 0 !important; bottom: 0 !important; width: 210mm !important; height: 297mm !important; margin: 0 !important; padding: 0 !important; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
    `,
  });

  if (!rawDoc) return null;

  const badge = BADGE_COLOR[rawDoc.type] || BADGE_COLOR.quotation;
  const cfg   = DOC_CFG[rawDoc.type]    || DOC_CFG.quotation;

  // Map API fields → PrintDoc shape
  const doc = {
    type:     rawDoc.type || 'quotation',
    number:   rawDoc.number || '',
    date:     rawDoc.date_str || '',
    customer: {
      name:    rawDoc.customer_name    || '',
      phone:   rawDoc.customer_phone   || '',
      address: rawDoc.customer_address || '',
    },
    items:   Array.isArray(rawDoc.items) ? rawDoc.items : [],
    advance: rawDoc.advance || '',
    terms:   Array.isArray(rawDoc.terms) ? rawDoc.terms : [],
  };

  const SCALE = 0.75;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(0,0,0,0.78)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '16px', overflowY: 'auto',
      }}
    >
      {/* Modal card */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
          width: 'fit-content', maxWidth: '100%',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Toolbar */}
        <div style={{
          background: '#0a1f5c', padding: '14px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              background: badge.bg, color: badge.color,
              borderRadius: 7, padding: '3px 12px', fontSize: 13, fontWeight: 800,
            }}>
              {cfg.label}
            </span>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{rawDoc.number}</span>
            {rawDoc.customer_name && (
              <span style={{ color: '#93c5fd', fontSize: 13 }}>
                <i className="fas fa-building me-1" style={{ fontSize: 11 }}></i>
                {rawDoc.customer_name}
              </span>
            )}
            {rawDoc.date_str && (
              <span style={{ color: '#64748b', fontSize: 12, background: 'rgba(255,255,255,0.1)', padding: '2px 8px', borderRadius: 5 }}>
                {fmtDocDate(rawDoc.date_str)}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button
              onClick={handlePrint}
              style={{
                background: '#2563eb', color: '#fff', border: 'none',
                borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <i className="fas fa-print"></i> Print / PDF
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none',
                borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <i className="fas fa-times"></i> Close
            </button>
          </div>
        </div>

        {/* Document preview */}
        <div style={{ background: '#e2e8f0', padding: '20px', overflowX: 'auto' }}>
          <div style={{
            width: Math.round(794 * SCALE),
            height: Math.round(1123 * SCALE),
            overflow: 'hidden',
            position: 'relative',
          }}>
            <div style={{
              width: 794,
              transform: `scale(${SCALE})`,
              transformOrigin: 'top left',
            }}>
              <PrintDoc ref={printRef} doc={doc} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
